import { useState, useMemo } from 'react';
import type { Order } from '../../types/ordering.types';
import { OrderStatus, isOverdue } from '../../types/ordering.types';
import {
  OrderListTable,
  COL_REF, COL_DATE, COL_DESC, COL_SUPPLIER, COL_EXP_DELIVERY, COL_TOTAL, COL_STATUS, COL_REQUESTER,
} from './OrderListTable';

type AllOrdFilter = 'all' | 'overdue' | 'ordered' | 'partial' | 'delivered';

const FILTERS: { key: AllOrdFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'partial', label: 'Partial' },
  { key: 'delivered', label: 'Delivered' },
];

interface Props {
  orders: Order[];
  isLoading: boolean;
  isOMGroup: boolean;
  onOpenOrder: (order: Order) => void;
  search: string;
}

export function AllOrders({ orders, isLoading, isOMGroup, onOpenOrder, search }: Props) {
  const [filter, setFilter] = useState<AllOrdFilter>('all');

  const filtered = useMemo(() => {
    let list = orders;
    if (filter === 'overdue') list = list.filter(isOverdue);
    else if (filter === 'ordered')   list = list.filter(o => o.status === OrderStatus.Ordered);
    else if (filter === 'partial')   list = list.filter(o => o.status === OrderStatus.PartialDelivery);
    else if (filter === 'delivered') list = list.filter(o => o.status === OrderStatus.Delivered);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(o =>
        o.title.toLowerCase().includes(q) ||
        o.orderRef.toLowerCase().includes(q) ||
        o.requesterName.toLowerCase().includes(q) ||
        o.supplier.toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, filter, search]);
 
  const columns = [
    COL_REF(), COL_DATE('orderDate', 'ORDERED'), COL_DESC(), COL_SUPPLIER(),
    COL_EXP_DELIVERY(), COL_TOTAL(), COL_STATUS(), COL_REQUESTER(),
  ];

  return (
    <div className="ord-view-content">
      <div className="info-box blue">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>
          <strong>All Orders</strong> — read-only view for all staff.
          {isOMGroup
            ? ' OM Group members (Sophie, Simon) can edit any order.'
            : ' OM Group members can edit any order.'}
          {' '}Default sort: expected delivery date, overdue items first.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-tab${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="ord-empty"><div className="spinner" /> Loading orders…</div>
      ) : (
        <OrderListTable
          orders={filtered}
          columns={columns}
          onRowClick={onOpenOrder}
          emptyMessage="No orders match the current filter."
        />
      )}
    </div>
  );
}
